<template>
  <div class="space-y-4">
    <div v-if="run" class="grid grid-cols-5 gap-4">
      <div class="rounded-md border border-gray-200 p-4"><span class="block text-gray-500">Status</span><HermesBadge :value="run.status" /><span v-if="run.error" class="block text-red-600 mt-1">{{ run.error }}</span></div>
      <div class="rounded-md border border-gray-200 p-4"><span class="block text-gray-500">Passed</span><span class="text-lg text-gray-900 tabular-nums">{{ passLine(run) || '—' }}</span></div>
      <div class="rounded-md border border-gray-200 p-4"><span class="block text-gray-500">Judge average</span><span class="text-lg text-gray-900 tabular-nums">{{ judgeAvg }}</span></div>
      <div class="rounded-md border border-gray-200 p-4"><span class="block text-gray-500">Cost</span><span class="text-lg text-gray-900 tabular-nums">{{ money((run.usage || {}).cost_usd) }}</span></div>
      <div class="rounded-md border border-gray-200 p-4"><span class="block text-gray-500">Time</span><span class="text-lg text-gray-900 tabular-nums">{{ duration(run.started_at, run.finished_at) }}</span></div>
    </div>

    <div class="rounded-md border border-gray-200 overflow-hidden">
      <table class="w-full table-fixed">
        <colgroup><col style="width:22%"><col style="width:9%"><col style="width:24%"><col style="width:9%"><col style="width:8%"><col style="width:12%"><col style="width:8%"><col style="width:8%"></colgroup>
        <thead class="bg-gray-50"><tr>
          <th :class="TH">Case</th><th :class="TH">Result</th><th :class="TH">Checks</th><th :class="[TH, 'text-right']">Judge</th><th :class="[TH, 'text-right']">Calls</th><th :class="TH">Hats</th><th :class="[TH, 'text-right']">Cost</th><th :class="[TH, 'text-right']">Open</th>
        </tr></thead>
        <tbody class="divide-y divide-gray-200">
          <tr v-if="!rows.length"><td colspan="8" class="px-3 py-8 text-center text-gray-500">{{ run && run.status === 'failed' ? 'The run failed before any case ran.' : 'Waiting for the first case…' }}</td></tr>
          <template v-for="row in rows" :key="row.key">
            <tr class="cursor-pointer hover:bg-neutral-50" @click="toggle(row.key)">
              <td :class="TD"><span class="font-medium">{{ row.label }}</span><span v-if="row.child.error" class="block text-red-600">{{ row.child.error }}</span></td>
              <td :class="TD"><HermesBadge v-if="!row.data" :value="row.child.status" /><span v-else :class="row.data.pass ? 'text-green-700' : 'text-red-700'">{{ row.data.pass ? 'pass' : `${row.data.passed} / ${row.data.checks_total}` }}</span></td>
              <td :class="TD">
                <span v-for="(c, name) in (row.data || {}).checks || {}" :key="name" :title="`${name}: ${c.detail}`" :class="['inline-block rounded px-1.5 mr-1 mb-1', c.ok ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800']">{{ name }}</span>
              </td>
              <td :class="[TD, 'text-right tabular-nums']">{{ row.data && row.data.judge_score !== null ? `${row.data.judge_score} / 5` : '—' }}</td>
              <td :class="[TD, 'text-right tabular-nums']">{{ row.data ? row.data.tool_calls : '—' }}</td>
              <td :class="TD"><span class="truncate block">{{ row.data ? (row.data.hats || []).join(', ') || '—' : '—' }}</span></td>
              <td :class="[TD, 'text-right tabular-nums']">{{ money((row.child.usage || {}).cost_usd) }}</td>
              <td :class="[TD, 'text-right']" @click.stop>
                <router-link v-if="sessionOf(row)" :to="{ name: 'Agent', params: { sessionId: sessionOf(row) } }" target="_blank" :class="TEXT_BUTTON">Session</router-link>
              </td>
            </tr>
            <tr v-if="open.has(row.key) && row.data">
              <td colspan="8" class="px-4 py-4 bg-neutral-50">
                <div class="grid grid-cols-12 gap-6">
                  <div class="col-span-5">
                    <span class="block text-gray-500 mb-1">Judge</span>
                    <p class="text-gray-900 sensitive" data-demo-blur>{{ row.data.judge_verdict || '—' }}</p>
                    <ul class="list-disc ml-5 text-gray-700 sensitive" data-demo-blur><li v-for="(r, i) in row.data.judge_reasons" :key="i">{{ r }}</li></ul>
                    <span class="block text-gray-500 mt-3 mb-1">Checks</span>
                    <ul class="text-gray-700"><li v-for="(c, name) in row.data.checks" :key="name"><span :class="c.ok ? 'text-green-700' : 'text-red-700'">{{ c.ok ? '✓' : '✕' }}</span> {{ name }}: {{ c.detail }}</li></ul>
                    <span class="block text-gray-500 mt-3 mb-1">Tools, in order</span>
                    <p class="text-gray-700 break-words">{{ (row.data.tools || []).join(' → ') || '—' }}</p>
                    <span class="block text-gray-500 mt-3 mb-1">Tables</span>
                    <p class="text-gray-700 break-words">{{ (row.data.tables || []).join(', ') || '—' }}</p>
                    <span v-if="(row.data.proposals || []).length" class="block text-gray-500 mt-3 mb-1">Writes proposed and declined</span>
                    <p v-if="(row.data.proposals || []).length" class="text-gray-700">{{ row.data.proposals.map((p) => p.kind).join(', ') }}</p>
                  </div>
                  <div class="col-span-7">
                    <span class="block text-gray-500 mb-1">Reply</span>
                    <pre class="whitespace-pre-wrap text-gray-900 font-sans sensitive" data-demo-blur>{{ row.data.reply || '(no reply text)' }}</pre>
                  </div>
                </div>
              </td>
            </tr>
          </template>
        </tbody>
      </table>
    </div>
    <p v-if="error" class="text-red-600">{{ error }}</p>
  </div>
</template>

<script>
import HermesBadge from '../HermesBadge.vue';
import { API, TD, TEXT_BUTTON, TH, duration, errorOf, money } from '../hermes';
import { passLine } from './bench';

export default {
  name: 'TestRunResults',
  components: { HermesBadge },
  props: { bench: { type: Object, required: true }, runId: { type: Number, required: true } },
  data() {
    return { run: null, children: [], results: [], open: new Set(), error: '', timer: null, TD, TEXT_BUTTON, TH };
  },
  computed: {
    rows() {
      const byKey = Object.fromEntries(this.results.map((r) => [r.unit_key, r.data]));
      return this.children.map((c) => ({ key: c.unit_key, label: (byKey[c.unit_key] || {}).label || c.unit_key, child: c, data: byKey[c.unit_key] || null }));
    },
    judgeAvg() {
      const j = (this.run?.metadata || {}).judge_avg;
      return j === null || j === undefined ? '—' : `${j} / 5`;
    },
  },
  mounted() {
    this.load();
  },
  beforeUnmount() {
    clearTimeout(this.timer);
  },
  methods: {
    duration, money, passLine,
    sessionOf(row) {
      return (row.child.metadata || {}).session_id || (row.data || {}).session_id || '';
    },
    toggle(key) {
      const next = new Set(this.open);
      if (next.has(key)) next.delete(key); else next.add(key);
      this.open = next;
    },
    async load() {
      clearTimeout(this.timer);
      const id = this.runId;
      try {
        const [run, kids, res] = await Promise.all([
          this.$axios.get(`${API}/runs/${id}/`),
          this.$axios.get(`${API}/runs/`, { params: { parent: id, limit: 100, sort: 'oldest' } }),
          this.$axios.get(`${API}/runs/${id}/results/`, { params: { limit: 100 } }),
        ]);
        this.run = run.data;
        this.children = kids.data.results;
        this.results = res.data.results;
        if (['pending', 'running'].includes(this.run.status)) this.timer = setTimeout(this.load, 4000);
      } catch (e) {
        this.error = errorOf(e, 'The run could not be loaded.');
      }
    },
  },
};
</script>
